import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { getConsent, saveConsent, loadGoogleAnalytics } from '../lib/analytics';

export default function CookieConsent() {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      setVisible(true);
    } else if (consent.analytics) {
      loadGoogleAnalytics();
    }
    const open = () => {
      const c = getConsent();
      setAnalytics(c ? !!c.analytics : true);
      setCustomize(true);
      setVisible(true);
    };
    window.addEventListener('open-cookie-preferences', open);
    return () => window.removeEventListener('open-cookie-preferences', open);
  }, []);

  if (!visible) return null;

  const close = (analyticsChoice) => {
    saveConsent(analyticsChoice);
    setVisible(false);
    setCustomize(false);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-[#1C1917] text-white p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          <Cookie className="w-6 h-6 text-[#B8860B] flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1">{t({ it: 'La tua privacy conta', en: 'Your privacy matters' })}</p>
            <p className="text-sm text-white/70 leading-relaxed">
              {t({
                it: 'Utilizziamo cookie tecnici necessari al funzionamento del sito e, solo con il tuo consenso, cookie statistici (Google Analytics) per capire come viene utilizzato. Puoi modificare le tue scelte in qualsiasi momento.',
                en: 'We use technical cookies necessary for the site to work and, only with your consent, analytics cookies (Google Analytics) to understand how it is used. You can change your choices at any time.',
              })}{' '}
              <Link to="/cookie-policy" className="underline hover:text-white" data-testid="cookie-policy-link">
                Cookie Policy
              </Link>
              {' · '}
              <Link to="/privacy" className="underline hover:text-white">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>

        {customize && (
          <div className="mb-4 space-y-2 pl-9" data-testid="cookie-customize-panel">
            <label className="flex items-center justify-between gap-4 py-2 border-b border-white/10">
              <div>
                <p className="text-sm font-medium">{t({ it: 'Cookie tecnici (necessari)', en: 'Technical cookies (necessary)' })}</p>
                <p className="text-xs text-white/60">{t({ it: 'Sessione, carrello, autenticazione. Sempre attivi.', en: 'Session, cart, authentication. Always active.' })}</p>
              </div>
              <input type="checkbox" checked disabled className="w-5 h-5 accent-[#4A5D23]" />
            </label>
            <label className="flex items-center justify-between gap-4 py-2 cursor-pointer">
              <div>
                <p className="text-sm font-medium">{t({ it: 'Cookie statistici (Google Analytics)', en: 'Analytics cookies (Google Analytics)' })}</p>
                <p className="text-xs text-white/60">{t({ it: 'Ci aiutano a capire come viene usato il sito, con IP anonimizzato.', en: 'Help us understand site usage, with anonymized IP.' })}</p>
              </div>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                data-testid="cookie-analytics-toggle"
                className="w-5 h-5 accent-[#4A5D23]"
              />
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-end">
          {!customize && (
            <button
              onClick={() => setCustomize(true)}
              data-testid="cookie-customize-btn"
              className="px-5 py-2.5 text-sm border border-white/30 hover:border-white transition-colors"
            >
              {t({ it: 'Personalizza', en: 'Customize' })}
            </button>
          )}
          <button
            onClick={() => close(false)}
            data-testid="cookie-reject-btn"
            className="px-5 py-2.5 text-sm border border-white/30 hover:border-white transition-colors"
          >
            {t({ it: 'Rifiuta', en: 'Reject' })}
          </button>
          {customize ? (
            <button
              onClick={() => close(analytics)}
              data-testid="cookie-save-btn"
              className="px-5 py-2.5 text-sm bg-[#4A5D23] hover:bg-[#3B4A1C] transition-colors"
            >
              {t({ it: 'Salva preferenze', en: 'Save preferences' })}
            </button>
          ) : (
            <button
              onClick={() => close(true)}
              data-testid="cookie-accept-btn"
              className="px-5 py-2.5 text-sm bg-[#4A5D23] hover:bg-[#3B4A1C] transition-colors"
            >
              {t({ it: 'Accetta tutti', en: 'Accept all' })}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
